/**
 * 1on1 の開設 / 共有相手の編集モーダル
 *
 * 本人と開設者は常に participants に含まれる（サーバー側で補完）ため、ここで選ぶのは
 * 追加で共有する相手（サブリーダー等）だけ。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering, HR
 * @graph-business 1on1 ペアの開設と共有相手（サブリーダー等）の編集モーダル
 * @graph-connects /one-on-one/pairs [calls] ペア開設 API
 * @graph-connects /one-on-one/pairs/participants [calls] 共有相手更新 API
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { MemberPicker } from '../../ui/MemberPicker/MemberPicker.js';
import { ApiError } from '../../../lib/api.js';
import {
  useCreateOneOnOnePair,
  useUpdateOneOnOneParticipants,
} from '../../../hooks/useOneOnOne.js';

/** @graph-connects none */
interface OneOnOneParticipantsModalProps {
  open: boolean;
  onClose: () => void;
  /** 対象メンバー */
  memberEmail: string;
  /** 既存ペアがあれば共有相手の編集、無ければ開設 */
  pairId: string | null;
  /** 既存の participants（本人・開設者を除いた共有相手を初期値にする） */
  initialParticipants: readonly string[];
  /** 常に含まれるため選択肢から外すメール（本人・開設者） */
  fixedEmails: readonly string[];
}

/**
 * API エラーを利用者向け文言にする
 * @graph-connects none
 */
function messageOf(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    return error.message || fallback;
  }
  return fallback;
}

/** @graph-connects none */
export function OneOnOneParticipantsModal({
  open,
  onClose,
  memberEmail,
  pairId,
  initialParticipants,
  fixedEmails,
}: OneOnOneParticipantsModalProps) {
  const { t } = useTranslation();
  const fixed = new Set(fixedEmails.map((email) => email.toLowerCase()));
  const [selected, setSelected] = useState<string[]>(() =>
    initialParticipants.filter((email) => !fixed.has(email.toLowerCase())),
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const createPair = useCreateOneOnOnePair();
  const updateParticipants = useUpdateOneOnOneParticipants();
  const isPending = createPair.isPending || updateParticipants.isPending;
  const isCreate = pairId === null;

  const handleSubmit = () => {
    setErrorMessage(null);
    const onError = (error: unknown) => {
      setErrorMessage(messageOf(error, t('oneOnOne.errors.saveFailed')));
    };
    if (isCreate) {
      createPair.mutate({ memberEmail, participants: selected }, { onSuccess: onClose, onError });
      return;
    }
    updateParticipants.mutate(
      { pairId, participants: [...fixedEmails, ...selected] },
      { onSuccess: onClose, onError },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isCreate ? t('oneOnOne.modal.createTitle') : t('oneOnOne.modal.editTitle')}
      description={t('oneOnOne.modal.description')}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            {t('actions.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={isPending}>
            {isCreate ? t('oneOnOne.modal.createSubmit') : t('oneOnOne.modal.editSubmit')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <MemberPicker
          multiple
          value={selected}
          onChange={setSelected}
          label={t('oneOnOne.modal.participantsLabel')}
          helperText={t('oneOnOne.modal.participantsHelp')}
          isMemberDisabled={(member) => fixed.has((member.email ?? member.id).toLowerCase())}
          aria-label={t('oneOnOne.modal.participantsLabel')}
        />
        {errorMessage && (
          <p role="alert" className="text-sm text-[var(--color-error)]">
            {errorMessage}
          </p>
        )}
      </div>
    </Modal>
  );
}
